import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Link,
  Snackbar,
  styled,
} from "@mui/material";
import Button from "../../../components/Button";
import TextBox from "../../../components/TextField";
import { useNavigate } from "react-router-dom";
import User from "../../../assets/icons/User.png";
import Mail from "../../../assets/icons/Mail.png";
import Phone from "../../../assets/icons/Phone.png";
import Lock from "../../../assets/icons/Lock.png";
import Routes from "../../../utils/Routes";
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  signInWithPhoneNumber,
  updateProfile,
  RecaptchaVerifier,
} from "firebase/auth";
import { auth } from "../../../firebase";
import { CallApiWithToken, callTokenApi } from "../../../api/ApiRequest";
import Alert from "../../../components/Alert";
import { api_createAccount } from "../../../api/APIEndpoint";
import { Unlock } from "react-feather";
import signUpSchema from "../../../Schema/SignUp.Schema";

const SignUpScreen = ({ className }) => {
  const navigate = useNavigate();

  // State Management
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  // Validation Rules
  const hasUppercase = /^[a-zA-Z]*(\s[a-zA-Z]*)+$/.test(name.trim());
  const hasSpecialSymbol = /[!@#$%^&*()_+{}\[\]:;<>,.?~\\\/-]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasAlphabet = /[A-Za-z]/.test(password);

  const signUpData = {
    name: name,
    email: email,
    phone: phone,
    password: password,
    confirmPassword: confirmPassword,
  };

  const isValidPhoneNumber = (phone) => {
    const sanitizedPhone = phone.replace(/[^0-9]/g, "");
    return sanitizedPhone.length === 10 && !/^(\d)\1{9}$/.test(sanitizedPhone);
  };

  const setUpRecaptcha = () => {
    window.recaptchaVerifier = new RecaptchaVerifier(auth, "sign-in-button", {
      size: "invisible",
    });
  };

  const handleSignUp = async () => {
    const { error } = signUpSchema.validate(signUpData);
    if (error) {
      error.details.forEach((err) => {
        setAlert({
          open: true,
          message: err.message,
          severity: "warning",
        });
      });
      return;
    }

    if (!isValidPhoneNumber(phone)) {
      setAlert({
        open: true,
        message: "Please enter a valid phone number.",
        severity: "warning",
      });
      return;
    }

    try {
      setIsLoading(true);
      setUpRecaptcha();

      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        setAlert({
          open: true,
          message: "Email already exists! Please log in.",
          severity: "warning",
        });
        setIsLoading(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      await callTokenApi();

      const apiResponse = await CallApiWithToken(
        "POST",
        `${api_createAccount}`,
        {
          FullName: name,
          PhoneNumber: phone,
          Email: email,
        }
      );

      const referralCode = apiResponse;

      localStorage.setItem("Token", userCredential.user.uid);

      localStorage.setItem("ReferralCode", referralCode);

      await updateProfile(userCredential.user, {
        displayName: `${name}#${referralCode}`,
      });

      setUpRecaptcha();
      const appVerifier = window.recaptchaVerifier;

      const confirmation = await signInWithPhoneNumber(
        auth,
        `+1${phone}`,
        appVerifier
      );
      setConfirmationResult(confirmation);
      setAlert({
        open: true,
        message: "OTP Sent Successfully. Please verify your phone.",
        severity: "success",
      });

      // Navigate to OTP verification page
      setTimeout(() => {
        navigate("/" + Routes.PhoneNumberVerification, {
          state: {
            verificationId: confirmation.verificationId,
            phone,
          },
        });
      }, 3000);
    } catch (error) {
      setAlert({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" className={className}>
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h4" className="headingText">
          Create Your Profile
        </Typography>
        <Typography variant="subtitle1" className="subHeadingText">
          Please enter your profile details
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <TextBox
          placeholder="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 30))}
          icon={User}
        />
        <Typography
          variant="caption"
          color={hasUppercase ? "textSecondary" : "error"}
        >
          First and last name must be seperated by space.
        </Typography>

        <TextBox
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value.toLowerCase().slice(0, 40))}
          icon={Mail}
        />

        <TextBox
          placeholder="Phone Number"
          value={phone}
          onChange={(e) =>
            setPhone(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
          }
          icon={Phone}
        />

        <TextBox
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value.slice(0, 16))}
          icon={
            showPassword ? (
              <Unlock color={"rgba(127, 168, 135, 1)"} size={24} />
            ) : (
              Lock
            )
          }
          onIconClick={() => setShowPassword(!showPassword)}
        />
        <Typography variant="caption" color="textSecondary">
          Password must be at least 8 characters and include{" "}
          <span style={{ color: hasNumber ? "#797979" : "red" }}>1 number</span>
          ,{" "}
          <span style={{ color: hasAlphabet ? "#797979" : "red" }}>
            1 letter
          </span>
          , and{" "}
          <span style={{ color: hasSpecialSymbol ? "#797979" : "red" }}>
            1 symbol
          </span>
          .
        </Typography>

        <TextBox
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value.slice(0, 16))}
          icon={
            showConfirmPassword ? (
              <Unlock color={"rgba(127, 168, 135, 1)"} size={24} />
            ) : (
              Lock
            )
          }
          onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
        />

        <button id="sign-in-button" style={{ display: "none" }}></button>

        <Button
          label="Create an account"
          onClick={handleSignUp}
          loading={isLoading}
          disabled={isLoading}
        />

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="body2">
            <span className="subHeadingText1">Already have an account?</span>{" "}
            <Link
              className="subHeadingText2"
              underline="hover"
              href={Routes.LogIn}
            >
              Login
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* MUI Alert Snackbar */}
      <Alert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </Container>
  );
};

export default styled(SignUpScreen)(({}) => ({
  ".container": {
    flex: 1,
    backgroundColor: "white",
  },
  ".headingContainer": {
    margin: 36,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  ".headingText": {
    fontWeight: "700",
    fontFamily: "LeagueSpartan",
    color: "rgba(35, 64, 0, 1)",
  },
  ".subHeadingText": {
    fontWeight: 400,
    fontFamily: "LeagueSpartan-Regular",
  },
  ".subHeadingText1": {
    fontFamily: "LeagueSpartan",
    fontWeight: 300,
    fontSize: 20,
  },
  ".subHeadingText2": {
    fontFamily: "LeagueSpartan",
    fontWeight: 600,
    color: "#234000",
    fontSize: 20,
  },
}));
